import { ContentColumn, LearnerActivityBase } from '../../../components/shared';
import { Card, FlexColMd } from '../../../../../components/Elements';
import QuestionnaireComplete from './QuestionnaireComplete';
import Notification, { NotificationType } from '../../../../../components/notifiers/Notification';
import strings from '../../../../../strings/strings.json';
import StartQuestionnaireCard from './StartQuestionnaireCard';
import QuestionnaireQuestionForm from './QuestionnaireQuestionForm';
import React, { useContext, useState } from 'react';
import { LearnerActivity } from '../../../../../models/LearnerCourse';
import { LearnerQuestion } from '../../../../../models/LearnerQuestionnaire';
import styled from 'styled-components';
import { sizes } from '../../../../../theme';
import { logError } from '../../../../../lib/debug-helpers';
import { ApiResponse } from '../../../../../lib/_api-helpers';
import { QuestionnaireAnswer } from '../../../../../models/QuestionnaireAnswer';
import { CourseNavigationActivity } from '../../../components/navigation/CourseNavigationActivity';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import { ErrorNotifier } from 'components/notifiers/ErrorNotifier';
import ActivityHeader from '../ActivityHeader';

type LearnerQuestionnaireContentProps = {
    questionnaire: LearnerActivity;
    currentQuestionNumber: number | null;
    currentQuestion: LearnerQuestion | undefined;
    setQuestionNumber: (newQuestionNumber: number | null) => void;
    answerQuestion: (answer: string) => Promise<ApiResponse<QuestionnaireAnswer>>;
    setActivityToComplete: () => Promise<ApiResponse<boolean>>;
    completionErrors: boolean;
};

export default function LearnerQuestionnaireContent({
    questionnaire,
    setActivityToComplete,
    answerQuestion,
    setQuestionNumber,
    currentQuestionNumber,
    currentQuestion,
    completionErrors,
}: LearnerQuestionnaireContentProps): JSX.Element {
    const { setCourseState, previewMode } = useContext(LearnerCourseContext);

    const [questionnairePreviouslyCompleted, setQuestionnairePreviouslyCompleted] = React.useState<boolean>(false);
    const [questionnaireCompleted, setQuestionnaireCompleted] = React.useState<boolean>(false);
    const [errors, setErrors] = useState<boolean>(false);

    React.useEffect(() => {
        if (questionnaire.completed) setQuestionnairePreviouslyCompleted(true);
    }, [questionnaire]);

    const submitAnswer = async (answer: string): Promise<boolean> => {
        if (!previewMode) {
            const response = await answerQuestion(answer);
            if (response.errors && response.errors.length > 0) {
                logError(response.errors);
                setErrors(true);
                return false;
            }
        }

        setCourseState((prev) => {
            if (!prev) return prev;

            const moduleIndex = prev.modules.findIndex((module) => module.id === questionnaire.moduleId);

            const activityIndex = prev.modules[moduleIndex].activities.findIndex(
                (activity) => activity.activityStatusId === questionnaire.activityStatusId,
            );

            const questionIndex = prev.modules[moduleIndex].activities[activityIndex].questions.findIndex(
                (question) => question.id === currentQuestion?.id,
            );

            const newModules = [...prev.modules];
            const newActivities = [...newModules[moduleIndex].activities];
            const newQuestions = [...newActivities[activityIndex].questions];

            newQuestions[questionIndex] = {
                ...newQuestions[questionIndex],
                answer,
            };

            newActivities[activityIndex] = {
                ...newActivities[activityIndex],
                questions: newQuestions,
            };

            newModules[moduleIndex] = {
                ...newModules[moduleIndex],
                activities: newActivities,
            };

            return {
                ...prev,
                modules: newModules,
            };
        });

        return true;
    };

    const nextQuestion = async (answer: string) => {
        const submitSuccess = await submitAnswer(answer);
        if (!submitSuccess) return false;

        if (currentQuestionNumber === null) {
            setQuestionNumber(0);
            return true;
        }

        if (currentQuestionNumber < questionnaire.questionsTotal - 1) {
            setQuestionNumber(currentQuestionNumber === null ? 0 : currentQuestionNumber + 1);
            return true;
        }

        const response = await setActivityToComplete();

        if (response.errors && response.errors.length > 0) {
            logError(response.errors);
            setErrors(true);
            return false;
        }

        setQuestionnaireCompleted(true);
        return true;
    };

    const previousQuestion = async (answer: string) => {
        if (currentQuestionNumber === null) return true;

        const submitSuccess = await submitAnswer(answer);
        if (!submitSuccess) return false;

        if (currentQuestionNumber === 0) {
            setQuestionNumber(null); //back to start
        } else {
            setQuestionNumber(currentQuestionNumber - 1);
        }

        return true;
    };

    if (!questionnaire)
        return (
            <ContentColumn>
                <ErrorNotifier
                    title={strings.questionnairePage.errors.title}
                    description={strings.questionnairePage.errors.description}
                />
            </ContentColumn>
        );

    if (completionErrors || errors) {
        return (
            <ContentColumn>
                <Notification
                    notificationType={NotificationType.WARNING}
                    title={strings.questionnairePage.errors.title}
                    description={strings.questionnairePage.errors.description}
                />
            </ContentColumn>
        );
    }

    return (
        <LearnerActivityBase>
            <ContentColumn>
                <ActivityHeader activity={questionnaire} />

                {questionnaireCompleted && <QuestionnaireComplete />}
                {!questionnaireCompleted && questionnairePreviouslyCompleted && (
                    <AnswersAlreadySubmittedContainer>
                        <Notification
                            notificationType={NotificationType.SUCCESS}
                            title={strings.questionnairePage.questionnaireAlreadyCompletedTitle}
                            description={strings.questionnairePage.questionnaireAlreadyCompletedDescription}
                        />
                    </AnswersAlreadySubmittedContainer>
                )}
                {!questionnaireCompleted && !questionnairePreviouslyCompleted && (
                    <QuestionnaireCard>
                        {currentQuestionNumber === null ? (
                            <StartQuestionnaireCard
                                totalQuestions={questionnaire.questionsTotal}
                                startQuestionnaire={() => setQuestionNumber(0)}
                                questionnaireTitle={questionnaire.title}
                            />
                        ) : (
                            <QuestionnaireQuestionForm
                                totalQuestions={questionnaire.questionsTotal}
                                currentQuestionNumber={currentQuestionNumber}
                                currentQuestion={currentQuestion}
                                firstQuestion={currentQuestionNumber === 0}
                                lastQuestion={currentQuestionNumber === questionnaire.questionsTotal - 1}
                                nextQuestion={nextQuestion}
                                previousQuestion={previousQuestion}
                            />
                        )}
                    </QuestionnaireCard>
                )}
            </ContentColumn>
            <Footer>
                <CourseNavigationActivity
                    questionnaireActive={
                        currentQuestion && !questionnaireCompleted && !questionnairePreviouslyCompleted
                    }
                />
            </Footer>
        </LearnerActivityBase>
    );
}

const AnswersAlreadySubmittedContainer = styled(FlexColMd)`
    align-items: flex-start;
`;

const QuestionnaireCard = styled(Card)`
    height: fit-content;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: ${sizes.spacingMd};
`;

const Footer = styled.div`
    width: 100%;
`;
